<template>
  <v-form ref="form" v-model="valid" validate-on="input lazy">
    <div class="info-block px-3 py-1 mb-6 d-flex align-top">
      <v-icon size="24" icon="$warning" />
      <span class="body2 ml-2" v-html="$t('modals.addEndpointDgExclusion.infoText')" />
    </div>

    <v-radio-group
      v-model="localValue.item.type"
      data-testid="dg-exclusion-type-radio-group"
      class="mb-2"
      inline
    >
      <v-radio
        class="mr-6"
        :label="$t('modals.addEndpointDgExclusion.typeFile')"
        :value="EndpointDgExclusionType.FILE"
        data-testid="dg-exclusion-type-file"
      />
      <v-radio
        :label="$t('modals.addEndpointDgExclusion.typeFolder')"
        :value="EndpointDgExclusionType.FOLDER"
        data-testid="dg-exclusion-type-folder"
      />
    </v-radio-group>

    <div class="subtitle1 mb-4">{{ $t("modals.addEndpointDgExclusion.pathLabel") }}</div>
    <v-combobox
      v-model="pathInput"
      class="mb-6"
      data-testid="dg-exclusion-path-input"
      :label="$t('modals.addEndpointDgExclusion.pathFieldLabel')"
      :aria-label="$t('modals.addEndpointDgExclusion.pathFieldLabel')"
      :placeholder="
        selectedEnvVar
          ? $t('modals.addEndpointDgExclusion.pathPlaceholderEnvVar')
          : $t('modals.addEndpointDgExclusion.pathPlaceholder')
      "
      :items="selectedEnvVar ? [] : availableEnvVars"
      item-title="label"
      item-value="key"
      :return-object="false"
      :rules="pathRules"
      :active="!!selectedEnvVar"
      :hint="
        selectedEnvVar
          ? $t('modals.addEndpointDgExclusion.pathHintEnvVar')
          : $t('modals.addEndpointDgExclusion.pathHint')
      "
      persistent-hint
      variant="outlined"
      menu-icon=""
      hide-no-data
      @update:model-value="onEnvVarSelected"
    >
      <template #menu-header>
        <div class="px-4 py-2 caption text-indigo-medium">
          {{ $t("modals.addEndpointDgExclusion.envVariables.menuHeader") }}
        </div>
      </template>
      <template v-if="selectedEnvVar" #prepend-inner>
        <v-chip
          class="align-self-center mr-1"
          color="indigo-faint"
          variant="flat"
          size="small"
          closable
          close-icon="$close"
          data-testid="dg-exclusion-env-var-chip"
          @click:close="removeEnvVar"
        >
          <span class="coro-label text-primary text-uppercase">{{ selectedEnvVar }}</span>
        </v-chip>
      </template>
      <template #item="{ item, props }">
        <v-list-item v-bind="props">
          <template #append>
            <v-tooltip location="top">
              <template #activator="{ props: tooltipProps }">
                <v-icon v-bind="tooltipProps" icon="$info" size="20" />
              </template>
              <span>{{ item.raw.tooltip }}</span>
            </v-tooltip>
          </template>
        </v-list-item>
      </template>
    </v-combobox>

    <div class="subtitle1 mb-4">
      {{ $t("modals.addEndpointDgExclusion.descriptionTitle") }}
      <span class="font-weight-regular">({{ $t("general.optional") }})</span>
    </div>
    <v-text-field
      v-model="localValue.item.description"
      data-testid="dg-exclusion-description-input"
      :label="$t('modals.addEndpointDgExclusion.descriptionLabel')"
      :aria-label="$t('modals.addEndpointDgExclusion.descriptionLabel')"
      :placeholder="$t('modals.addEndpointDgExclusion.descriptionPlaceholder')"
      variant="outlined"
    />
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import cloneDeep from "lodash/cloneDeep";
import {
  EndpointDgExclusionEnvVariable,
  EndpointDgExclusionType,
} from "@/constants/endpoint-data-governance";
import type { AddEndpointDgExclusionPayload } from "@/_store/endpoint-data-governance/exclusions.module";
import Patterns from "@/constants/patterns";
import { required } from "@/_helpers/validators";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<{ action: string }>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const valid = ref<boolean | null>(null);
    const form = ref();
    const pathInput = ref<string | null>(null);
    const selectedEnvVar = ref<string | null>(null);

    const localValue = ref<{ item: AddEndpointDgExclusionPayload; action: string }>({
      item: {
        path: "",
        type: EndpointDgExclusionType.FILE,
        description: "",
      },
      action: props.config.action,
    });

    const availableEnvVars = computed(() => [
      {
        key: EndpointDgExclusionEnvVariable.USERPROFILE,
        label: EndpointDgExclusionEnvVariable.USERPROFILE,
        tooltip: i18n.t("modals.addEndpointDgExclusion.envVariables.tooltipUserprofile"),
      },
      {
        key: EndpointDgExclusionEnvVariable.TEMP,
        label: EndpointDgExclusionEnvVariable.TEMP,
        tooltip: i18n.t("modals.addEndpointDgExclusion.envVariables.tooltipTemp"),
      },
    ]);

    const pathRules = computed(() => [
      required(),
      (v: string | null) => {
        if (!v || !selectedEnvVar.value) return true;
        const trimmed = v.trim();
        if (!trimmed.startsWith("\\") && !trimmed.startsWith("/")) {
          return i18n.t("modals.addEndpointDgExclusion.pathEnvVarError");
        }
        return true;
      },
      (v: string | null) => {
        if (!v) return true;
        const trimmed = v.trim();
        const fullPath = selectedEnvVar.value ? `${selectedEnvVar.value}${trimmed}` : trimmed;
        if (!Patterns.FILE_PATH(fullPath)) {
          return i18n.t("modals.addEndpointDgExclusion.pathError");
        }
        return true;
      },
    ]);

    const onEnvVarSelected = (value: string) => {
      const isEnvVar = Object.values(EndpointDgExclusionEnvVariable).includes(
        value as EndpointDgExclusionEnvVariable
      );
      if (isEnvVar) {
        selectedEnvVar.value = value;
        pathInput.value = null;
      }
    };

    const removeEnvVar = () => {
      selectedEnvVar.value = null;
      pathInput.value = null;
    };

    const validate = async () => {
      const result = await form.value.validate();
      return result.valid;
    };

    watch([pathInput, selectedEnvVar], ([newPath, newEnvVar]) => {
      const newValue = cloneDeep(localValue.value);
      const trimmedPath = (newPath ?? "").trim();
      newValue.item.path = newEnvVar ? `${newEnvVar}${trimmedPath}` : trimmedPath;
      localValue.value = newValue;
    });

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", cloneDeep(newVal));
      },
      { deep: true, immediate: true }
    );

    watch(
      valid,
      (newVal) => {
        if (newVal === null) return;
        emit("update:valid", newVal);
      },
      { immediate: false }
    );

    return {
      localValue,
      valid,
      form,
      pathInput,
      pathRules,
      selectedEnvVar,
      availableEnvVars,
      onEnvVarSelected,
      removeEnvVar,
      EndpointDgExclusionType,
      validate,
    };
  },
});
</script>
